import {
	AppManager,
	type AppManagerType,
} from "@/lib/audio/application/AppManager";
import type {
	AppStoreProps,
	ModalIds,
} from "@/lib/audio/application/stores/appStore";
import { UserAnalytics } from "@/lib/userAnalytics";
import { useFeatureFlagVariantKey, usePostHog } from "posthog-js/react";
import { createContext, useContext, useMemo } from "react";

interface AppContextProps {
	appStore: AppStoreProps;
	audioManager: AppManagerType["audioManager"];
	authManager: AppManagerType["authManager"];
	featureFlags: Record<string, string | boolean | undefined>;
	isSubscribed: boolean;
	openedModalId: ModalIds;
	paymentManager: AppManagerType["paymentManager"];
	userAnalytics: UserAnalytics | null;
	handleModalOpen: (modalId: ModalIds, isVisible: boolean) => void;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const useApp = () => {
	const context = useContext(AppContext);
	if (!context) {
		throw new Error("useApp must be used within an AppProvider");
	}
	return context;
};

const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const appManager = AppManager.getInstance();
	const { openedModalId, isUserSubscribed } = appManager.useStore();
	const appStore = appManager.useStore();
	const { audioManager, authManager, paymentManager } = appManager;
	const postHog = usePostHog();

	// setup our feature flag variant keys
	const featureFlags = {};

	let userAnalytics: UserAnalytics | null = null;
	if (postHog) {
		// Initialize UserAnalytics with PostHog if available
		userAnalytics = UserAnalytics.getInstance(postHog);
	}

	const handleModalOpen = (modalId: ModalIds, isVisible: boolean) => {
		appManager.showModal(modalId, isVisible);
	};

	return (
		<AppContext.Provider
			value={{
				appStore,
				audioManager,
				authManager,
				featureFlags,
				isSubscribed: isUserSubscribed,
				openedModalId,
				handleModalOpen,
				paymentManager,
				userAnalytics,
			}}
		>
			{children}
		</AppContext.Provider>
	);
};

export default AppProvider;
